<script setup>
import Cookies                       from 'js-cookie';
import { RouterLink, RouterView }   from 'vue-router';
import { onMounted, ref, watch }    from 'vue';
import { useRoute, useRouter }      from 'vue-router';
import { useUserStore }             from '@/stores/user';
import { useAuth }                  from '@/composables/useAuth';
import { useMenuInit }              from '@/composables/useMenuInit';
import { useScreenSize }            from '@/composables/useScreenSize';
import menus                        from '@/menu';
import NotificationListener         from '@/notifications/views/NotificationListener.vue';
import ticketOptions                from '@/tickets/views/ticketOptions.vue';
import Publications                 from '@/advertising_notifications/views/publications.vue';
import ChatProjectAI                from '@/project_ai/chat.vue';
import UserSetupGuide               from '@/components/userSetupGuide.vue';
import { iconsMap }                 from './utils/icons';
import { LogOut, ArrowBigLeft }     from 'lucide-vue-next';

const route     = useRoute();
const router    = useRouter();
const userStore = useUserStore();

// ── Composables ───────────────────────────────────────────────
const { isAuthenticated, type_user, logout, authStore } = useAuth();
const { menu, type_menu, show_publications, initMenu }  = useMenuInit(isAuthenticated);
const { screenWidth, show_menu, show_menu_close, is_collapse } = useScreenSize();

// ── Datos auxiliares ──────────────────────────────────────────
const menuInitial = ref(menus['initial']);
const user_info   = ref(userStore.user_info);

watch(() => userStore.user_info, (val) => user_info.value = val);

// ── Inicialización ────────────────────────────────────────────
onMounted(() => {
    // Si el store dice autenticado pero la cookie ya expiró, limpiar estado stale
    if (authStore.isAuthenticated && !Cookies.get('isAuthenticated')) {
        authStore.logout();
        return;
    }
    initMenu();
});
</script>

<template>
  <div class="layout">

    <!-- ################## SIDEBAR - 100% height ################### -->
    <el-menu
      class="el-menu-vertical-demo scroll-menu sidebar"
      default-active="0"
      :collapse="is_collapse"
      v-if="isAuthenticated && show_menu && !route.meta.hideLayout"
      popper-effect="light"
    >

      <div class="sidebar-header">
        <img v-if="!is_collapse" src="@/assets/image/logos/logo_blanco.png" alt="logo" class="sidebar-logo" />
        <span class="burger-lines" @click="is_collapse = !is_collapse">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </div>

      <div v-if="type_menu == 'admin'">
        <div v-for="(item, index) in menu" :key="index">
          <RouterLink :to="item.path" v-if="item.is_menu && item.show" :class="'system-'+item.name">
            <el-menu-item :index="String(index)">
              <component class="menu-icon" :is="iconsMap[item.icon]" :size="20" style="min-width: 20px; min-height: 20px;" />
              <template #title>
                <span class="ml-2">{{ item.label }}</span>
              </template>
            </el-menu-item>
          </RouterLink>
        </div>
      </div>

      <div v-if="type_menu == 'sucursal' || type_menu == 'sponsor' || type_menu == 'vendedor'">
        <div v-for="(item, index) in menu" :key="index">
          <RouterLink :to="item.path" v-if="item.is_menu && item.show && item.label == 'Menú principal' && type_user == 'admin'" :class="'system-'+item.name">
            <el-menu-item :index="String(index)">
              <component class="menu-icon" :is="iconsMap[item.icon]" :size="20" style="min-width: 20px; min-height: 20px;" />
              <template #title>
                <span class="ml-2">{{ item.label }}</span>
              </template>
            </el-menu-item>
          </RouterLink>
        </div>
        <div v-for="(item, index) in menu" :key="'sub-' + index">
          <el-sub-menu :index="String(index)" v-if="item.label == 'Menú interno'">
            <template #title>
              <component class="menu-icon" :is="iconsMap[item.icon]" :size="20" style="min-width: 20px; min-height: 20px; color: var(--el-color-primary-light)" />
              <span v-if="!is_collapse" class="sub-menu-span">{{ item.label }}</span>
            </template>
            <div v-for="(children, index_children) in menu" :key="index_children">
              <RouterLink v-if="children.is_menu && children.label != 'Menú principal'" :to="children.path" :class="'system-'+children.name">
                <el-menu-item :index="String(index + '-' + index_children)">
                  <component class="menu-icon" :is="iconsMap[children.icon]" :size="16" style="color: var(--el-color-primary-light)" />
                  <span class="ml-1"> {{ children.label }} </span>
                </el-menu-item>
              </RouterLink>
            </div>
          </el-sub-menu>
        </div>
      </div>

      <el-menu-item @click="logout">
        <LogOut :size="20" style="min-width: 20px; min-height: 20px;" />
        <template #title>
          <button class="ml-2">Cerrar sesión</button>
        </template>
      </el-menu-item>
    </el-menu>

    <!-- ################## DERECHA: navbar + contenido ################### -->
    <div id="container-menu-content">

      <!-- NAVBAR TOP -->
      <nav class="navbar" role="navigation" aria-label="main navigation" v-if="!route.meta.hideLayout">
        <div class="container-nav">

          <!-- MOBILE MENU -->
          <div class="mobile-menu" v-if="!show_menu && isAuthenticated">
            <el-menu default-active="0" class="el-menu-demo" mode="horizontal" :ellipsis="false">
              <el-sub-menu index="1" style="background: var(--color-veterinary)">
                <template #title>
                  <el-button class="navbar-burger">
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                    <span aria-hidden="true"></span>
                  </el-button>
                </template>
                <div v-if="type_menu == 'admin'">
                  <div v-for="(item, index) in menu" :key="index">
                    <RouterLink :to="item.path" v-if="item.is_menu && item.show" :class="'system-'+item.name">
                      <el-menu-item :index="String(index)">
                        <component class="menu-icon pr-2" :is="iconsMap[item.icon]" :size="20" />
                        <template #title>
                          <span class="ml-2">{{ item.label }}</span>
                        </template>
                      </el-menu-item>
                    </RouterLink>
                  </div>
                </div>
                <div v-if="type_menu == 'sucursal' || type_menu == 'sponsor' || type_menu == 'vendedor'">
                  <div v-for="(item, index) in menu" :key="index">
                    <RouterLink :to="item.path" v-if="item.is_menu && item.show && item.label == 'Menú principal' && type_user == 'admin'" :class="'system-'+item.name">
                      <el-menu-item :index="String(index)">
                        <component class="menu-icon" :is="iconsMap[item.icon]" :size="20" />
                        <template #title>
                          <span class="ml-2">{{ item.label }}</span>
                        </template>
                      </el-menu-item>
                    </RouterLink>
                  </div>
                  <div v-for="(item, index) in menu" :key="'sub-' + index">
                    <el-sub-menu :index="String(index)" v-if="item.label == 'Menú interno'">
                      <template #title>
                        <component class="menu-icon" :is="iconsMap[item.icon]" :size="20" />
                        <span class="sub-menu-span">{{ item.label }}</span>
                      </template>
                      <div v-for="(children, index_children) in menu" :key="index_children">
                        <RouterLink :to="children.path" v-if="children.is_menu && children.label != 'Menú principal'" :class="'system-'+children.name">
                          <el-menu-item :index="index + '-' + index_children">
                            {{ children.label }}
                          </el-menu-item>
                        </RouterLink>
                      </div>
                    </el-sub-menu>
                  </div>
                </div>
                <el-menu-item>
                  <LogOut :size="20" class="mr-3" />
                  <template #title>
                    <button @click="logout">Cerrar sesión</button>
                  </template>
                </el-menu-item>
              </el-sub-menu>
            </el-menu>
          </div>

          <!-- MENU SIN AUTH MOBILE -->
          <el-menu default-active="1" class="el-menu-demo" mode="horizontal" :ellipsis="false"
            v-if="!show_menu_close && !isAuthenticated">
            <el-sub-menu index="2" style="background: var(--color-veterinary)">
              <template #title>
                <el-button class="navbar-burger">
                  <span aria-hidden="true"></span>
                  <span aria-hidden="true"></span>
                  <span aria-hidden="true"></span>
                  <span aria-hidden="true"></span>
                </el-button>
              </template>
              <div v-for="(item, index) in menuInitial" :key="index">
                <RouterLink :to="item.path" v-if="item.show == true">
                  <el-menu-item class="mt-1 mb-1">{{ item.label }}</el-menu-item>
                </RouterLink>
              </div>
            </el-sub-menu>
          </el-menu>

          <div class="main-title">
            <ArrowBigLeft v-if="route.meta.is_children" class="menu-icon pr-2 is-clickable has-text-light" :size="26" @click="router.back()" />
            <component class="menu-icon pr-2 has-text-light" :is="iconsMap[route.meta.icon]" :size="24" />
            <h2 class="has-text-light">{{ route.meta.title }}</h2>
          </div>

          <div class="icon-notifications">
            <div v-if="isAuthenticated && show_publications">
              <Publications ref="ComponentPublicationsGeneral" />
            </div>
            <div v-if="isAuthenticated && (type_menu == 'admin' || type_menu == 'sucursal') && user_info.role?.name !== 'client_system_default_25cce0a1-574f'">
              <UserSetupGuide />
            </div>
            <div class="is-hidden-mobile" v-if="isAuthenticated && user_info.role?.name !== 'client_system_default_25cce0a1-574f'">
              <ticketOptions />
            </div>
            <div v-if="isAuthenticated">
              <NotificationListener />
            </div>
            <div v-if="isAuthenticated">
              <span class="user-name is-hidden-mobile has-text-light">{{ user_info.name ?? user_info.contact_name }}</span>
            </div>
          </div>
        </div>

        <div id="navbarBasicExample" class="navbar-menu">
          <div class="navbar-end">
            <div id="withoutAuth" class="link-navbar">
              <ul class="menu-active" id="second-menu" v-if="!isAuthenticated">
                <li v-for="(menu, index) in menuInitial" :key="index">
                  <RouterLink v-if="menu.show == true" :to="menu.path">{{ menu.label }}</RouterLink>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>

      <!-- CONTENIDO PRINCIPAL -->
      <el-main
        :class="[
          route.meta.hideLayout
            ? 'full-content'
            : (isAuthenticated ? 'auth-content' : 'content main-login')
        ]"
      >
        <Layout :class="isAuthenticated ? (screenWidth ? 'mobile-class' : 'desktop-class') : 'no-auth-desktop-class'">
          <RouterView />
        </Layout>
        <ChatProjectAI v-if="isAuthenticated && !route.meta.hideLayout && (type_menu == 'admin' || type_menu == 'sucursal')" />
      </el-main>

    </div>
  </div>
</template>

<style scoped>
.layout {
  display: flex;
  flex-direction: row;
  height: 100vh;
  overflow: hidden;
}

.full-content {
  width: 100%;
  margin: 0;
  padding: 0;
}

.sidebar {
  height: 100vh;
  overflow-y: auto;
  flex-shrink: 0;
}

#container-menu-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

nav .container-nav {
  display: flex;
  background-color: var(--el-color-primary);
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 1rem;
}

.el-main {
  flex: 1;
  overflow-y: auto;
}

.user-name {
  font-size: 14px;
}

.icon-notifications {
  display: flex;
  gap: 1rem;
  right: 1.5rem;
}

.main-title {
  display: flex;
  align-items: center;
  height: 100%;
  padding-left: 1rem;
  color: var(--el-color-primary);
}

.main-title .menu-icon {
  color: #5da5cc;
}

.main-title h2 {
  font-size: 1.5rem;
  font-weight: 500;
}

#second-menu {
  display: flex;
  gap: 1rem;
}

.auth-content {
  height: calc(100vh - 55px);
  flex: 1;
  padding: 0 0 2rem 0;
  background-image: url('@/assets/image/background/back.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  overflow-y: auto !important;
  overflow-x: hidden;
}

.content {
  min-height: 100vh;
  background-color: #fff;
}

.el-menu-vertical-demo {
  height: 100vh;
}

.el-menu-vertical-demo:not(.el-menu--collapse) {
  width: 280px;
}

.menu-icon-color {
  color: white !important;
}

.el-menu-item.is-active.menu-icon-color {
  color: var(--el-menu-active-color) !important;
}

.el-sub-menu.is-active.menu-icon-color {
  color: var(--el-menu-active-color) !important;
}

.el-menu {
  padding: 0 5px 5px 5px;
}

.el-menu-item {
  color: var(--el-color-primary-light) !important;
  font-weight: 600;
  border-radius: 10px !important;
}

.sub-menu-span {
  color: var(--el-color-primary-light) !important;
  padding-left: 7px;
}

.el-menu-item.is-active {
  background: #5cadd8 !important;
  border-radius: 10px !important;
  color: white !important;
}

.el-sub-menu.is-active {
  background: #5cadd8 !important;
  border-radius: 10px !important;
  color: white !important;
}

.el-menu-item:hover {
  background-color: var(--el-color-primary-light) !important;
  color: var(--el-color-primary) !important;
  border-radius: 10px !important;
}

.el-menu-item:hover .menu-icon {
  color: var(--el-color-primary) !important;
}

.mobile-class {
  padding: .5rem !important;
  display: block;
}

.desktop-class {
  padding: 1rem 2.5rem 0 2rem !important;
  display: block;
}

.no-auth-desktop-class {
  height: 100%;
  padding: 0 !important;
  margin: 0 !important;
}

.navbar-burger {
  color: white;
  background: var(--el-color-primary) !important;
  border: 1px solid var(--el-color-primary);
}

.navbar-burger:hover {
  border: 1px solid white;
}

.menu-active a {
  color: var(--white-10);
}

.menu-active a:hover {
  color: var(--black-80);
}

.menu-active a.router-link-exact-active {
  color: var(--black-90);
}

.menu-active a.router-link-exact-active:hover {
  color: var(--black-90);
}

.burger-sidebar {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.burger-lines {
  display: flex;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
}

.burger-lines span {
  display: block;
  width: 22px;
  height: 2px;
  background: white;
  border-radius: 2px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.sidebar-logo {
  width: 120px;
  object-fit: contain;
}

.navbar-menu {
  background-color: var(--el-color-primary);
}

@media (max-width: 768px) {
  .container-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 1rem;
  }

  .main-title h2 {
    font-size: 1.1rem;
  }

  .icon-notifications {
    position: static;
  }

  .navbar-burger {
    color: var(--el-color-primary) !important;
    background: white !important;
  }
}
</style>